import { CorrespondenceLanguages, PhoneNumberCountries } from '@/models/VariousEnum';


/**
 * Configuration extracted from .env file for an easy access.
 */
// List of supported SU (e.g: vs, fr)
export const SUPPORTED_SU = process.env.VUE_APP_SUPPORTED_SU.split(',');

// Configuration regarding app languages/locales
export const SUPPORTED_LANGUAGES = process.env.VUE_APP_SUPPORTED_LANGUAGES.split(',');

// Base de toutes les route de l'API
export const PUBLIC_ROOT_API = process.env.VUE_APP_PUBLIC_ROOT_API;

// App version used to force refresh the app when new update is available
export const APP_VERSION = process.env.VUE_APP_VERSION.trim();

const ACCEPTING_TERMS: any = {
  fr: true,
  vs: false,
  gr: false,
  bb: false,
  ti: false,
  sg: false,
  be: false,
  so: true,
  lu: false,
};

export function checkAcceptingTerms(suName: string): boolean {
  const su: string = suName.toLowerCase();
  if (!ACCEPTING_TERMS.hasOwnProperty(su)) {
    return false; // default value for each SU
  }
  return ACCEPTING_TERMS[su];
}

/**
 * Laguage disponibles pour chaque su
 * @date 17.08.2023 - 11:21:17
 *
 * @type {*}
 */
const CORRESPONDENCE_LANGUAGES_MAPPING: any = {
  fr: ['fr', 'de'],
  vs: ['fr', 'de'],
  gr: ['de', 'it'],
  bb: ['de'],
  ti: ['it'],
  sg: ['de'],
  be: ['de', 'fr'],
  so: ['de'],
  lu: ['de'],
};

/**
 * Gestion du mappage de la langue pour chaque su
 * @date 17.08.2023 - 11:22:36
 *
 * @export
 * @param {string} suName - Le nom du su
 * @returns {*} - Un tableau qui contient la liste des langue du su qui a été passé en paramètre
 */
export function getCorrespondenceLanguagesBySu(suName: string): any {
  const su: string = suName;

  if (!CORRESPONDENCE_LANGUAGES_MAPPING.hasOwnProperty(su)) {
    return [];
  }

  const languages = CORRESPONDENCE_LANGUAGES_MAPPING[su];
  const correspondenceLang = [];

  languages.forEach((element: string) => {
    correspondenceLang.push(CorrespondenceLanguages[element.toUpperCase() as keyof typeof CorrespondenceLanguages]);
  });
  return languages;
}


/**
 * Langue par défaut pour les su qui emploient le site publique
 * @date 16.08.2023 - 13:06:24
 *
 * @type {*}
 */
const FALLBACK_LANGUAGE_MAPPING: any = {
  fr: 'fr',
  vs: 'fr',
  gr: 'de',
  bb: 'de',
  ti: 'it',
  sg: 'de',
  be: 'de',
  so: 'de',
  lu: 'de',
}

/**
 * Méthode qui va faire le lien entre le su et la langue par défaut
 * @date 16.08.2023 - 13:07:22
 *
 * @export
 * @param {string} su -- le su se trouvant dans l'url
 * @returns {string} -- la langue par défaut du su qui a été passé en paramètre
 */
export function getLanguageFallBackBySu(su: string): string {
  if (!FALLBACK_LANGUAGE_MAPPING.hasOwnProperty(su)) {
    return '';
  }
  return FALLBACK_LANGUAGE_MAPPING[su];
}


/**
 * Phone numbers configuration for each SU
 */
const PHONE_NUMBER_COUNTRIES_MAPPING: any = {
  fr: ['ch'],
  vs: ['ch', 'fr', 'it'],
  gr: ['ch'],
  bb: ['ch'],
  ti: ['ch'],
  sg: ['ch'],
  be: ['ch'],
  so: ['ch'],
  lu: ['ch'],
};

export function getPhoneNumberCountriesBySu(suName: string): string[] {
  const su: string = suName.toLowerCase();
  if (!PHONE_NUMBER_COUNTRIES_MAPPING.hasOwnProperty(su)) {
    return [];
  }

  const countries = PHONE_NUMBER_COUNTRIES_MAPPING[su];
  const phoneNumberCountry = [];

  countries.forEach((element: string) => {
    phoneNumberCountry.push(PhoneNumberCountries[element.toUpperCase() as keyof typeof PhoneNumberCountries]);
  });

  return countries;
}

/**
 * Display checkbox to allow the results to be sent to the pharmacy for each SU
 */
const DISPLAY_SEND_RESULTS_TO_PHARMACY: any = {
  fr: false,
  vs: false,
  gr: false,
  bb: false,
  ti: false,
  sg: false,
  be: false,
  so: false,
  lu: false,
};

export function mustDisplaySendResultsToPharmacy(suName: string) {
  const su: string = suName.toLowerCase();
  if (!DISPLAY_SEND_RESULTS_TO_PHARMACY.hasOwnProperty(su)) {
    return false;
  }

  return DISPLAY_SEND_RESULTS_TO_PHARMACY[su];
}

/**
 * Fit only configuration for doctors for each SU.
 * Be careful, if you set this config to true, add an entry for the SU in DB and set it to true also!
 */
const DOCTOR_FIT_ONLY: any = {
  fr: false,
  vs: true,
  gr: false,
  bb: false,
  ti: false,
  sg: false,
  be: false,
  so: false,
  lu: false,
};

export function displayFitOnlyForDoctors(suName: string) {
  const su: string = suName.toLowerCase();
  if (!DOCTOR_FIT_ONLY.hasOwnProperty(su)) {
    return false;
  }

  return DOCTOR_FIT_ONLY[su];
}

/**
 * Screening units id and name
 */
const SCREENING_UNITS: any = {
  // PROD
  1: 'fr',
  3: 'vs',
  4: 'sg',
  10: 'ti',
  11: 'bb',
  13: 'gr',
  14: 'be', // And so
  15: 'lu',

  // TEST
  101: 'fr',
  103: 'vs',
  104: 'sg',
  110: 'ti',
  111: 'bb',
  113: 'gr',
  114: 'be', // And so
  115: 'lu',

  // DEV
  301: 'fr',
  303: 'vs',
  304: 'sg',
  310: 'ti',
  311: 'bb',
  313: 'gr',
  314: 'be', // And so
  315: 'lu',
};

export function getScreeningUnitNameFromID(suId: string) {
  const su: number = Number(suId);
  if (!SCREENING_UNITS.hasOwnProperty(su)) {
    return '';
  }
  return SCREENING_UNITS[su];
}

/**
 * Pro login activation
 */
const PRO_LOGIN_ACTIVATION: any = {
  fr: true,
  vs: true,
  gr: false,
  bb: true,
  ti: false,
  sg: false,
  be: true,
  so: true,
  lu: true,
};

export function isProLoginActivatedFromSU(suName: string) {
  const su: string = suName.toLowerCase();
  if (!PRO_LOGIN_ACTIVATION.hasOwnProperty(su)) {
    return false;
  }
  return PRO_LOGIN_ACTIVATION[su];
}

/**
 * Use simple consent
 */
const USE_SIMPLE_CONSENT: any = {
  fr: false,
  vs: false,
  gr: false,
  bb: true,
  ti: false,
  sg: false,
  be: true,
  so: true,
  lu: true,
};

export function isSimpleConsentUsedFromSU(suName: string) {
  const su: string = suName.toLowerCase();
  if (!USE_SIMPLE_CONSENT.hasOwnProperty(su)) {
    return false;
  }
  return USE_SIMPLE_CONSENT[su];
}

/**
 * Pharmacists make FIT orders instead of giving test directly
 */
const PHARMACIST_FIT_ORDER: any = {
  fr: false,
  vs: false,
  bb: true,
  be: true,
  so: true,
  lu: false,
};

export function isPharmacistFitOrderActivated(suName: string) {
  const su: string = suName.toLowerCase();
  if (!PHARMACIST_FIT_ORDER.hasOwnProperty(su)) {
    return false;
  }
  return PHARMACIST_FIT_ORDER[su];
}

/**
 * Coloscopy mode activation.
 * Allows access to public inclusion through xx/login/{fit|colonoscopy|refusal}.
 *  - in case of xx/login/fit allows a partiicpant to do a fit inclusion (normal behaviour)
 *  - in case of xx/login/colonoscopy allows a partiicpant to do a colo inclusion
 *  - in case of xx/login/refusal allows a partiicpant to go directly to the the alternative questionnaire
 */
const COLONOSCOPY_MODE_ACTIVATION: any = {
  fr: false,
  vs: false,
  gr: false,
  bb: true,
  ti: false,
  sg: false,
  be: true,
  so: true,
  lu: false,
};

export function isColonoscopyModeActivatedFromSU(suName: string) {
  const su: string = suName.toLowerCase();
  if (!COLONOSCOPY_MODE_ACTIVATION.hasOwnProperty(su)) {
    return false;
  }
  return COLONOSCOPY_MODE_ACTIVATION[su];
}

const ADVICE_MODE_ACTIVATION: any = {
  fr: false,
  vs: false,
  gr: false,
  bb: true,
  ti: false,
  sg: false,
  be: false,
  so: false,
  lu: false,
};

export function isAdviceModeActivatedFromSU(suName: string) {
  const su: string = suName.toLowerCase();
  if (!ADVICE_MODE_ACTIVATION.hasOwnProperty(su)) {
    return false;
  }
  return ADVICE_MODE_ACTIVATION[su];
}
